import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { StoriesFiltersQuery, StoriesPhotosModel } from 'account-hybrid/features/stories/models/stories-library.model';
import dayjs from 'dayjs/esm/index';
import utc from 'dayjs/esm/plugin/utc';
import { each, includes, reduce } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Parse } from 'shared';
import { BroadcastData, CurationAdvocateData, CurationModalData, PhotocontestData } from '../models/curation.model';

dayjs.extend(utc);

@Injectable({
  providedIn: 'root'
})
export class CurationService {

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_TOKEN) private environment: Environment
  ) { }

  getCurationModalData(propertyUUID: string, guestUUID: string): Observable<CurationModalData> {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/${propertyUUID}/guests/${guestUUID}/curation`;
    return this.http.get<CurationModalData>(url);
  }

  updateRate(propertyUUID: string, guestUUID: string, index: number, type: string) {
    let url;
    if (type === 'quote') {
      url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/${propertyUUID}/guests/${guestUUID}/${type}/rating`;
    } else {
      url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/layers/photo-advocacy/contests/photos/${guestUUID}/${type}/set`;
    }
    return this.http.put(url, index);
  }

  updatePhotoDetails(propertyUUID: string, guestUUID: string, data: {caption: string; quote: string; rotation: number;}) {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/layers/photo-advocacy/contests/photos/${guestUUID}/contest-photo-info/set`;
    return this.http.put(url, data);
  }

  advanceToCampaign(propertyUUID, photoId, data: boolean) {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/layers/photo-advocacy/contests/photos/${photoId}/finalist/set`;
    return this.http.put(url, data);
  }

  getCurationAdvocateData(propertyUUID: string, guestUUID: string): Observable<CurationAdvocateData> {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/${propertyUUID}/advocates/${guestUUID}`;
    return this.http.get<CurationAdvocateData>(url).pipe(
      map(data => this.transformDetailsResponse(data))
    );
  }

  getBroadcastData(propertyUUID: string, guestUUID: string): Observable<BroadcastData> {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/layers/photocontest/contests/${guestUUID}/data`;
    return this.http.get<BroadcastData>(url);
  }

  getPhotocontestData(propertyUUID: string): Observable<PhotocontestData> {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/${propertyUUID}/layers/photocontest`;
    return this.http.get<PhotocontestData>(url);
  }

  createShareImage(propertyUUID: string, model) {
    const url = `/api/${propertyUUID}/social-network-sharing/create-curation-image`;
    return this.http.post(url, model);
  }

  savePhotoReplacement(propertyUUID: string, guestUUID: string, data) {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/layers/photo-advocacy/contests/photos/${guestUUID}`;
    return this.http.post(url, data);
  }

  changeReservation(propertyUUID: string, guestUUID: string, data) {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/${propertyUUID}/advocates/${guestUUID}`;
    return this.http.put(url, data);
  }

  transformDetailsResponse(data) {

    data.totalFollowers = Parse.intOrDefault(data.totalFollowers);
    data.totalVisitors = Parse.intOrDefault(data.totalVisitors);
    data.totalSignups = Parse.intOrDefault(data.totalSignups);

    // add recent activity type
    let activityType;
    if (data.poststay && data.poststay.photo) {
      if (data.poststay.photo.shareDate) activityType = 'photoShare';
      else if (data.poststay.photo.voteDate) activityType = 'photoVote';
      else if (data.poststay.photo.finalistDate) activityType = 'photoFinalist';
      else if (data.poststay.photo.createDate) activityType = 'photoUpload';
    }
    else if (data.poststay && data.poststay.quote) {
      if (data.poststay.quote.createDate) activityType = 'quote';
    }
    else if (data.prestay) {
      if (data.prestay.epochDate) activityType = 'prestayShare';
    }

    data.lastActivityType = activityType;

    if (data.prestay) {
        data.prestay.promotions = Parse.arrayOrDefault(data.prestay.promotions);
        each(data.prestay.promotions, (promotion) => {
            promotion.totalFollowers = Parse.intOrDefault(promotion.totalFollowers);
        });
        this.fixPromotions(data.prestay.promotions);

        data.prestay.incentives = Parse.arrayOrDefault(data.prestay.incentives);
        each(data.prestay.incentives, (incentive) => {
            incentive.epochExpirationDate = Parse.int(incentive.epochExpirationDate);
            incentive.utcExpirationDate = dayjs(incentive.epochExpirationDate).utc();
        });
    }

    if (data.poststay) {
        data.poststay.promotions = Parse.arrayOrDefault(data.poststay.promotions);
        each(data.poststay.promotions, (promotion) => {
            promotion.totalFollowers = Parse.intOrDefault(promotion.totalFollowers);
        });
        this.fixPromotions(data.poststay.promotions);

        data.poststay.incentives = Parse.arrayOrDefault(data.poststay.incentives);
        each(data.poststay.incentives, (incentive) => {
            incentive.epochExpirationDate = Parse.int(incentive.epochExpirationDate);
            incentive.utcExpirationDate = dayjs(incentive.epochExpirationDate).utc();
        });
    }

    if (data.photoContest) {
        data.photoContest.photos = Parse.arrayOrDefault(data.photoContest.photos);
        data.photoContest.emailsSent = Parse.arrayOrDefault(data.photoContest.emailsSent);

        each(data.photoContest.photos, (photo) => {
            photo.promotions = Parse.arrayOrDefault(photo.promotions);
            photo.friendPromotions = Parse.arrayOrDefault(photo.friendPromotions);
            if(photo.winner){
                photo.winner.epochIncentiveExpirationDate = Parse.int(photo.winner.epochIncentiveExpirationDate);
                photo.winner.utcExpirationDate = dayjs(photo.winner.epochIncentiveExpirationDate).utc();
            }

            this.fixPromotions(photo.promotions);
            this.fixPromotions(photo.friendPromotions);
        });
    }

    return data;
  }

  fixPromotions(promotions) {
    let other = reduce(promotions, (memo, promotion) => {
      if (!includes(['fb', 'li', 'tw'], promotion.title)) {
        memo.totalFollowers += promotion.totalFollowers;
      }
      return memo;
    }, { title: 'Others', totalFollowers: 0 });
    if (other.totalFollowers != 0) {
      promotions.push(other);
    }
    return promotions;
  }

  sendEmailCertificate(propertyUUID: string, guestUUID: string, id: string, data = {}) {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/${propertyUUID}/guests/${guestUUID}/certificates/${id}/send-email`;
    return this.http.post(url, data);
  }

  saveCertificateExpirationDate(propertyUUID: string, guestUUID: string, id: string, data = {}) {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUUID}/properties/${propertyUUID}/guests/${guestUUID}/certificates/${id}`;
    return this.http.put(url, data);
  }

  sendRecommandation(propertyUUID: string, data) {
    const url = `${this.environment.apiBaseUrl}/${propertyUUID}/properties/${propertyUUID}/recommendations`;
    return this.http.post(url, data);
  }

  loadStoriesIsCuration(propertyUuid: string, tab: string, query?: StoriesFiltersQuery) {
    const params = new HttpParams()
      .set('SortBy', query['SortBy'])
      .set('SortDirection', query['SortDirection'])
      .set('Offset', query['Offset'])
      .set('Fetch', query['Fetch'])
      .set('SearchText', query['SearchText'] || '')
      .set('isCurrentOnly', query['isCurrentOnly'])
      .set('cutoffDate', query['cutoffDate']);
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUuid}/properties/${propertyUuid}/stories/${tab}`;
    return this.http.get<StoriesPhotosModel>(url, {params});
  }
}
